import { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Trophy, Diamond, CheckCircle, Buildings, Siren, UsersThree } from "@phosphor-icons/react";
import { listAchievements, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const CATEGORY_META = {
  calls: { label: "Calls", icon: Siren, color: "text-red-400" },
  stations: { label: "Stations", icon: Buildings, color: "text-emerald-400" },
  mutual_aid: { label: "Mutual Aid", icon: UsersThree, color: "text-orange-300" },
};

export default function AchievementsDialog({ open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const d = await listAchievements();
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  const grouped = (data?.achievements || []).reduce((acc, a) => {
    (acc[a.category] = acc[a.category] || []).push(a);
    return acc;
  }, {});

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="achievements-dialog"
        className="rounded-none border border-yellow-400/30 bg-[#0A0E14] text-white max-w-2xl p-0"
      >
        <DialogHeader className="px-5 py-4 border-b border-white/10">
          <DialogTitle className="mono uppercase tracking-widest text-[12px] text-yellow-300 flex items-center gap-2">
            <Trophy size={16} weight="fill" /> Achievements
          </DialogTitle>
          {data && (
            <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
              Unlocked:{" "}
              <span className="text-yellow-300" data-testid="ach-unlocked-count">
                {data.total_unlocked} / {data.total_count}
              </span>
            </div>
          )}
        </DialogHeader>

        <div className="px-5 py-4 space-y-5 max-h-[70vh] overflow-y-auto">
          {loading && (
            <div className="mono text-[11px] uppercase tracking-widest text-zinc-500">
              Loading…
            </div>
          )}
          {Object.entries(grouped).map(([category, items]) => {
            const meta = CATEGORY_META[category] || { label: category, icon: Trophy, color: "text-zinc-400" };
            const Icon = meta.icon;
            return (
              <section key={category}>
                <h3 className={`mono text-[10px] uppercase tracking-widest mb-2 flex items-center gap-1.5 ${meta.color}`}>
                  <Icon size={12} weight="fill" /> {meta.label}
                </h3>
                <div className="space-y-2">
                  {items.map((a) => (
                    <AchievementRow key={a.id} a={a} />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AchievementRow({ a }) {
  const pct = Math.min(100, Math.round((a.progress / a.threshold) * 100));
  return (
    <div
      data-testid={`ach-${a.id}`}
      className={`border p-3 flex items-center gap-3 ${
        a.unlocked
          ? "border-yellow-400/40 bg-yellow-400/[0.04]"
          : "border-white/10 bg-white/[0.02]"
      }`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className={`font-bold text-sm ${a.unlocked ? "text-yellow-200" : "text-zinc-200"}`}>
            {a.title}
          </span>
          {a.unlocked && (
            <CheckCircle size={13} weight="fill" className="text-yellow-300 shrink-0" />
          )}
        </div>
        <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
          {a.desc}
        </div>
        <div className="mt-2 flex items-center gap-2">
          <div className="flex-1 h-1.5 bg-white/5 overflow-hidden">
            <div
              className={`h-full transition-all duration-300 ${
                a.unlocked ? "bg-yellow-400" : "bg-cyan-400/70"
              }`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="mono text-[10px] uppercase tracking-widest text-zinc-500 w-16 text-right">
            {a.progress} / {a.threshold}
          </span>
        </div>
      </div>
      <div className="flex flex-col items-end shrink-0">
        <div className="mono text-[9px] uppercase tracking-widest text-zinc-500">Reward</div>
        <div className={`mono font-bold text-sm flex items-center gap-0.5 ${
          a.unlocked ? "text-yellow-300" : "text-cyan-300"
        }`}>
          <Diamond size={11} weight="fill" /> {a.reward}
        </div>
      </div>
    </div>
  );
}
